import { RefObject, useEffect } from 'react';

/**
 * Keep Spark/Radix modal dialogs open while the customer interacts with the
 * assistant. Radix's dismissable layer listens for pointerdown/focusin on the
 * document and treats anything outside the dialog content as an outside
 * interaction (closing the dialog). Stopping propagation at `document.body` —
 * after React's own delegated handlers (attached at the app root, below body)
 * have run — keeps assistant interactions out of that machinery without
 * breaking the assistant's own event handling. The matching
 * `pointer-events: auto` in the callers' styles undoes the body-level
 * pointer lock Radix applies while a modal is open.
 */
export function useDialogDismissShield(ref: RefObject<HTMLElement>, onEscape?: () => void) {
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const body = element.ownerDocument.body;
    const window = element.ownerDocument.defaultView;
    const escape = (event: KeyboardEvent) => {
      if (onEscape && event.key === 'Escape' && event.target instanceof Node && element.contains(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        onEscape();
      }
    };
    // Radix handles Escape at document capture, before React's bubbling handler.
    window?.addEventListener('keydown', escape, true);
    const stop = (event: Event) => {
      const target = event.target;
      // For focusout, the element RECEIVING focus is relatedTarget; Radix's
      // focus trap reacts to the dialog-side focusout when focus moves into
      // the assistant, so that hop must be shielded too or the trap yanks
      // focus straight back and typing becomes impossible.
      const related = (event as FocusEvent).relatedTarget;
      if (
        (target instanceof Node && element.contains(target)) ||
        (related instanceof Node && element.contains(related))
      ) {
        event.stopPropagation();
      }
    };
    const events: (keyof HTMLElementEventMap)[] = ['pointerdown', 'mousedown', 'touchstart', 'focusin', 'focusout'];
    events.forEach(name => body.addEventListener(name, stop));
    return () => {
      events.forEach(name => body.removeEventListener(name, stop));
      window?.removeEventListener('keydown', escape, true);
    };
  }, [ref, onEscape]);
}
